import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Check, ChevronRight, SkipForward } from 'lucide-react';
import { SlideOver } from '@/components/SlideOver.js';
import {
  completeChecklistItem,
  getTodayChecklist,
  updateChecklistNotes,
  type ChecklistType,
  type StoreChecklist,
} from '@/lib/api/checklists.js';
import { burstConfetti } from '@/lib/confetti-burst.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { cn } from '@/lib/utils.js';

function ChecklistPanel({
  type,
  onNavigateLink,
}: {
  type: ChecklistType;
  onNavigateLink?: () => void;
}) {
  const queryClient = useQueryClient();
  const wasCompleteRef = useRef(false);
  const query = useQuery({
    queryKey: ['checklist-today', type],
    queryFn: () => getTodayChecklist(type),
  });

  const completeMut = useMutation({
    mutationFn: (args: { checklistId: string; itemId: string; skipped?: boolean }) =>
      completeChecklistItem(args.checklistId, args.itemId, { skipped: args.skipped }),
    onSuccess: (updated) => {
      queryClient.setQueryData(['checklist-today', type], updated);
      queryClient.invalidateQueries({ queryKey: ['checklist-today'] });
      const nowComplete =
        updated.progress.total > 0 && updated.progress.completed >= updated.progress.total;
      if (nowComplete && !wasCompleteRef.current) {
        burstConfetti();
        toastSuccess(
          type === 'open'
            ? 'Opening checklist complete! Have a great day!'
            : 'Closing checklist complete! See you tomorrow!',
        );
      }
      wasCompleteRef.current = nowComplete;
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const notesMut = useMutation({
    mutationFn: (args: { checklistId: string; notes: string | null }) =>
      updateChecklistNotes(args.checklistId, args.notes),
    onSuccess: (updated) => {
      queryClient.setQueryData(['checklist-today', type], updated);
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const checklist = query.data;

  useEffect(() => {
    if (checklist) {
      wasCompleteRef.current =
        checklist.progress.total > 0 &&
        checklist.progress.completed >= checklist.progress.total;
    }
  }, [checklist?.id, checklist?.progress.completed, checklist?.progress.total]);

  if (query.isLoading) {
    return <p className="text-sm text-slate-500">Loading checklist…</p>;
  }
  if (!checklist || !checklist.id) {
    return (
      <p className="text-sm text-slate-500">
        {checklist && checklist.enabled === false
          ? 'This checklist is turned off for your store.'
          : 'Checklist unavailable.'}
      </p>
    );
  }

  const checklistId = checklist.id;

  return (
    <div className="space-y-4">
      <ChecklistItemsList
        checklist={checklist}
        pending={completeMut.isPending}
        onComplete={(itemId, skipped) =>
          completeMut.mutate({ checklistId, itemId, ...(skipped ? { skipped: true } : {}) })
        }
        {...(onNavigateLink ? { onNavigateLink } : {})}
      />
      <div>
        <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500">
          Notes
        </label>
        <textarea
          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-900"
          rows={3}
          placeholder="Add notes for today…"
          defaultValue={checklist.notes ?? ''}
          onBlur={(e) => {
            const next = e.target.value.trim() || null;
            if (next !== (checklist.notes ?? null)) {
              notesMut.mutate({ checklistId, notes: next });
            }
          }}
        />
      </div>
    </div>
  );
}

export function ChecklistItemsList({
  checklist,
  pending,
  onComplete,
  onNavigateLink,
}: {
  checklist: StoreChecklist;
  pending: boolean;
  onComplete: (itemId: string, skipped?: boolean) => void;
  onNavigateLink?: () => void;
}) {
  const { progress } = checklist;
  const pct = progress.total > 0 ? Math.round((progress.completed / progress.total) * 100) : 0;

  return (
    <div className="space-y-4">
      <div>
        <div className="mb-1 flex justify-between text-sm font-semibold text-slate-700">
          <span>
            {progress.completed}/{progress.total} complete
          </span>
          <span>{pct}%</span>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-slate-100">
          <div
            className={cn(
              'h-full rounded-full transition-all',
              progress.completed >= progress.total
                ? 'bg-green-600'
                : progress.completed === 0
                  ? 'bg-red-500'
                  : 'bg-amber-500',
            )}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      <ul className="space-y-2">
        {checklist.items.map((item) => {
          const done = item.completed || item.skipped;
          return (
            <li
              key={item.id}
              className={cn(
                'flex items-start gap-3 rounded-xl border px-3 py-3',
                done ? 'border-slate-200 bg-slate-50 opacity-80' : 'border-slate-200 bg-white',
              )}
            >
              <button
                type="button"
                disabled={pending || done}
                onClick={() => onComplete(item.id)}
                className={cn(
                  'mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md border',
                  done
                    ? item.skipped
                      ? 'border-slate-300 bg-slate-200 text-slate-500'
                      : 'border-green-600 bg-green-600 text-white'
                    : 'border-slate-300 hover:border-primary hover:bg-primary/5',
                )}
                title={item.skipped ? 'Skipped' : done ? 'Completed' : 'Mark complete'}
              >
                {done ? <Check className="h-3.5 w-3.5" /> : null}
              </button>
              <div className="min-w-0 flex-1">
                <p className={cn('text-sm font-medium', done && 'line-through text-slate-500')}>
                  {item.label}
                  {item.automated ? (
                    <span className="ml-2 rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-bold uppercase text-primary">
                      Auto
                    </span>
                  ) : null}
                </p>
                {item.link ? (
                  <Link
                    to={item.link.startsWith('/') ? item.link : `/${item.link}`}
                    onClick={onNavigateLink}
                    className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                  >
                    Go to task <ChevronRight className="h-3 w-3" />
                  </Link>
                ) : null}
              </div>
              {!done && !item.automated ? (
                <button
                  type="button"
                  disabled={pending}
                  onClick={() => onComplete(item.id, true)}
                  className="shrink-0 rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                  title="Skip"
                >
                  <SkipForward className="h-4 w-4" />
                </button>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function ChecklistSlideOver({
  open,
  onClose,
  initialType = 'open',
}: {
  open: boolean;
  onClose: () => void;
  initialType?: ChecklistType;
}) {
  const [tab, setTab] = useState<ChecklistType>(initialType);

  useEffect(() => {
    if (open) setTab(initialType);
  }, [open, initialType]);

  return (
    <SlideOver
      title="Daily Checklist"
      open={open}
      onClose={onClose}
      panelClassName="max-w-lg"
    >
      <div className="mb-4 flex gap-2 rounded-xl bg-slate-100 p-1">
        {(['open', 'close'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={cn(
              'flex-1 rounded-lg px-3 py-2 text-sm font-semibold capitalize',
              tab === t ? 'bg-white text-slate-950 shadow-sm' : 'text-slate-600',
            )}
          >
            {t === 'open' ? '🌅 Opening' : '🌙 Closing'}
          </button>
        ))}
      </div>
      <ChecklistPanel type={tab} onNavigateLink={onClose} />
    </SlideOver>
  );
}
